import { Person } from "@mui/icons-material";
import {
  Avatar as MuiAvatar,
  type AvatarProps as MuiAvatarProps,
} from "@mui/material";
import React, { useState, useEffect } from "react";

import { useAppSelector, useAppDispatch } from "../store";
import { selectAvatarUrl } from "../store/selectors/authSelectors";
import { loadAvatarUrl } from "../store/slices/authSlice";
import { logger } from "../utils/logger";

interface AvatarProps extends Omit<MuiAvatarProps, "src" | "alt"> {
  email: string;
  customAvatarUrl?: string | null;
  size?: number;
  name?: string;
  showPersonIcon?: boolean; // New prop to control fallback behavior
}

const Avatar: React.FC<AvatarProps> = ({
  email,
  customAvatarUrl,
  size = 40,
  name,
  showPersonIcon: _showPersonIcon = false,
  sx,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const cachedAvatarUrl = useAppSelector(selectAvatarUrl);
  const [imageError, setImageError] = useState(false);

  // Загружаем URL аватара из store или запрашиваем если нет
  useEffect(() => {
    // Загружаем аватар только если его еще нет в store
    if (!cachedAvatarUrl) {
      dispatch(loadAvatarUrl({ email, customAvatarUrl, size }));
    }
  }, [dispatch, email, customAvatarUrl, size, cachedAvatarUrl]);

  // Генерируем инициалы из имени или email
  const getInitials = (name?: string, email?: string): string => {
    if (name) {
      return name
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join("");
    }

    if (email) {
      return email.charAt(0).toUpperCase();
    }

    return "?";
  };

  const initials = getInitials(name, email);

  // Show initials while loading
  if (!cachedAvatarUrl) {
    return (
      <MuiAvatar sx={{ width: size, height: size, ...sx }} {...props}>
        {initials}
      </MuiAvatar>
    );
  }

  // If we have an avatar URL and no error, show the image
  if (cachedAvatarUrl && !imageError) {
    return (
      <MuiAvatar
        src={cachedAvatarUrl}
        alt={name || email}
        onError={() => {
          logger.warn("[avatar] Image failed to load", cachedAvatarUrl);
          setImageError(true);
        }}
        sx={{ width: size, height: size, ...sx }}
        {...props}
      >
        {initials}
      </MuiAvatar>
    );
  }

  // Fallback: show initials; if имени нет, иконка пользователя
  return (
    <MuiAvatar sx={{ width: size, height: size, ...sx }} {...props}>
      {initials ? initials : <Person sx={{ fontSize: size * 0.7 }} />}
    </MuiAvatar>
  );
};

export default Avatar;
